import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

const CHEER_COLORS = ["#B76E79", "#F7E7CE", "#F1E4D8", "#FAF9F6", "#0A0A0A"];
const GUEST_STORAGE_KEY = "hb_guest_cart_v1";

function fireCheer() {
  // school-cheer confetti — bursts from bottom-left + bottom-right
  const defaults = { spread: 65, ticks: 90, gravity: 0.9, decay: 0.94, startVelocity: 45, colors: CHEER_COLORS, zIndex: 90 };
  confetti({ ...defaults, particleCount: 60, angle: 60, origin: { x: 0, y: 1 } });
  confetti({ ...defaults, particleCount: 60, angle: 120, origin: { x: 1, y: 1 } });
  setTimeout(() => confetti({ ...defaults, particleCount: 40, angle: 90, origin: { x: 0.5, y: 0.9 }, spread: 120 }), 180);
}

// ─── Guest-cart storage helpers (localStorage-backed) ──────────────
function readGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function writeGuestCart(items) {
  try {
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(items));
  } catch { /* quota / private mode — ignore */ }
}
function clearGuestCart() {
  try { localStorage.removeItem(GUEST_STORAGE_KEY); } catch { /* noop */ }
}

/** Same shape the backend `GET /api/cart` returns, so downstream code (Cart
 * page, SideCart, CartLightbox) doesn't need to know whether the shopper
 * is signed in. */
async function hydrateGuestCart(rawItems) {
  const detailed = [];
  let subtotal = 0;
  await Promise.all(
    rawItems.map(async (it) => {
      try {
        const { data: product } = await api.product(it.product_id);
        // Pick the variant image that matches the selected color (mirrors
        // backend behavior in routes_commerce.get_cart).
        let image = product.images?.[0];
        for (const v of product.variants || []) {
          if (v.color === it.color && v.image) { image = v.image; break; }
        }
        const line_total = (product.price || 0) * (it.quantity || 1);
        subtotal += line_total;
        detailed.push({ ...it, product, image, line_total });
      } catch {
        // Product deleted or unavailable — drop it from the guest cart silently.
      }
    }),
  );
  return { items: detailed, subtotal: Math.round(subtotal * 100) / 100 };
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [open, setOpen] = useState(false);
  const prevUserRef = useRef(user); // used to detect login (null → user)

  const load = useCallback(async () => {
    if (user) {
      try {
        const { data } = await api.cart();
        setCart(data);
      } catch { /* ignore */ }
      return;
    }
    // Guest — hydrate from localStorage
    const raw = readGuestCart();
    const hydrated = await hydrateGuestCart(raw);
    setCart(hydrated);
  }, [user]);

  // Merge guest cart into server cart when the shopper signs in.
  useEffect(() => {
    const wasGuest = !prevUserRef.current;
    const nowUser = !!user;
    prevUserRef.current = user;
    if (!(wasGuest && nowUser)) { load(); return; }

    const guest = readGuestCart();
    if (guest.length === 0) { load(); return; }

    // Push each guest item to the server cart, then clear localStorage.
    (async () => {
      for (const it of guest) {
        try {
          await api.addCart({
            product_id: it.product_id,
            size: it.size || "",
            quantity: it.quantity || 1,
            color: it.color || null,
          });
        } catch { /* skip items that fail — sold-out / removed */ }
      }
      clearGuestCart();
      await load();
    })();
  }, [user, load]);

  const addItem = async (payload) => {
    if (user) {
      await api.addCart(payload);
      await load();
    } else {
      // Guest — dedupe by (product_id, size, color) and bump quantity
      const items = readGuestCart();
      const idx = items.findIndex(
        (i) => i.product_id === payload.product_id
          && (i.size || "") === (payload.size || "")
          && (i.color || null) === (payload.color || null),
      );
      if (idx >= 0) {
        items[idx].quantity = (items[idx].quantity || 1) + (payload.quantity || 1);
      } else {
        items.push({
          product_id: payload.product_id,
          size: payload.size || "",
          color: payload.color || null,
          quantity: payload.quantity || 1,
        });
      }
      writeGuestCart(items);
      await load();
    }
    fireCheer();
    setOpen(true);
  };

  const removeItem = async (product_id) => {
    if (user) {
      await api.removeCart(product_id);
    } else {
      const items = readGuestCart().filter((i) => i.product_id !== product_id);
      writeGuestCart(items);
    }
    await load();
  };

  const openCart = () => setOpen(true);
  const closeCart = () => setOpen(false);

  return (
    <CartContext.Provider value={{ cart, open, openCart, closeCart, addItem, removeItem, refresh: load, isGuest: !user }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
